import {ArrayUtils, ConfigObjectType, useApiQuery, useCurrentLocale} from '@wallaby-ui/core';
import {Option, UserFilterDto} from '@wallaby-ui/document';
import {useCallback, useMemo} from 'react';

import {ALERT_BOARD_ORIGIN_COMPONENTS} from './alertBoardFilters';

type UseAlertBoardAlertOptionsReturn = {
  options: Option[];
  isLoading: boolean;
  getAlertLabel: (id: number) => string;
};

/** The saved filters of one list, as the saved-filter box reads them (own and shared, with their alert state). */
function useComponentUserFilters(config: ConfigObjectType, component: string, enabled: boolean) {
  const url: string = config.service.url.userFilterGet;
  const params = new URLSearchParams({component});
  return useApiQuery<UserFilterDto[]>([url, String(params)], null, {
    axiosConfig: {url, params},
    reactQueryOptions: {enabled, staleTime: 0, refetchOnMount: 'always'},
  });
}

/**
 * The Alert filter's options (PDW-184, list page D3): a pick from the alerts the viewer may see, never free text, built
 * from the saved-filter lists of the components alerts are defined on. Every filter that is or was an alert is offered,
 * a retired one too, since Include retired alerts can list it. Labelled by the alert's name, sorted by it.
 */
export function useAlertBoardAlertOptions(config: ConfigObjectType, enabled: boolean): UseAlertBoardAlertOptionsReturn {
  const [hospitalizationComponent, recommendationComponent] = ALERT_BOARD_ORIGIN_COMPONENTS;
  const hospitalizations = useComponentUserFilters(config, hospitalizationComponent, enabled);
  const recommendations = useComponentUserFilters(config, recommendationComponent, enabled);
  const locale = useCurrentLocale();
  const alerts = useMemo(() => {
    const all = [...(hospitalizations.data ?? []), ...(recommendations.data ?? [])].filter((v) => v.alertState != null && v.id != null);
    return ArrayUtils.sortedByLocaleString(all, (v) => v.name, locale, {caseFirst: 'lower'});
  }, [hospitalizations.data, recommendations.data, locale]);
  const options = useMemo<Option[]>(() => alerts.map((v) => ({id: String(v.id), label: v.name})), [alerts]);
  const getAlertLabel = useCallback((id: number) => alerts.find((v) => v.id === Number(id))?.name ?? String(id), [alerts]);
  return {options, isLoading: hospitalizations.isLoading || recommendations.isLoading, getAlertLabel};
}
